import * as React from "react";
import {
    Stack, Divider, Button, Tooltip, Typography, Box,
    TableContainer,
    Paper
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PauseRounded from "@mui/icons-material/PauseRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { Localized } from "@fluent/react";
import { ProcessActionType, ProjectProcess } from "../../projectProcess/ProjectProcessApiClient";
import { EditIconButton } from "../../../../app/components/iconButtons/EditIconButton";
import PendingActionsRounded from "@mui/icons-material/PendingActionsRounded";
import CancelRounded from "@mui/icons-material/CancelRounded";
import { DeleteValidationProcess } from "../dialogs/DeleteValidationProcess";
import { AppId } from "../../../../app/appTypes";
import { useNavigate } from "react-router-dom";
import { EditProcessDescriptionField } from "../dialogs/EditProcessDescriptionField";
import { ValidationScenarioLabel } from "./ValidationScenarioLabel";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess } from "../../../notifications/notificationsSlice";
import { PerformActionOnProcessDialog } from "../dialogs/PerformActionOnProcessDialog";



type ValidationProcessManagementPanelProps = {
    process: ProjectProcess;
    onSave: (updated: ProjectProcess, refreshRequired?: boolean) => void;
};

type FieldKey = "no" | "name" | "scenario" | "description";

export const ValidationProcessManagementPanel: React.FC<ValidationProcessManagementPanelProps> = ({
    process,
    onSave,
}) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [editField, setEditField] = React.useState<FieldKey | null>(null);
    const [editValue, setEditValue] = React.useState<string>("");
    const [openDelete, setOpenDelete] = React.useState(false);
    const [openActionDialog, setOpenActionDialog] = React.useState(false);
    const [selectedAction, setSelectedAction] = React.useState<ProcessActionType | null>(null);

    const openEdit = (field: FieldKey, value?: string | null) => {
        setEditField(field);
        setEditValue(value ?? "");
    };

    const closeEdit = () => setEditField(null);


    const status = (process.status ?? "").toUpperCase();
    const isInProgress = status === "I";
    const isPaused = status === "H";
    const isFinished = status === "F" || status === "C";

    const canStart = !isInProgress && !isFinished;
    const canPause = isInProgress;
    const canFinish = !isFinished;
    const canDelete = isInProgress || isPaused;

    //Handlers 
    const handleProcessDeleted = React.useCallback((deletedId: AppId) => {
        setOpenDelete(false);
        navigate(`/validation-processes`)
    }, []);

    const openActionDialogFor = (actionName: ProcessActionType) => {
        setSelectedAction(actionName);
        setOpenActionDialog(true);
    };

    const closeActionDialog = () => {
        setOpenActionDialog(false);
        setSelectedAction(null);
    };

    type FieldRowProps = {
        field: FieldKey;
        labelId: string;
        value?: string | null;
        valueNode?: React.ReactNode;
    };


    const FieldRow = ({ field, labelId, value, valueNode }: FieldRowProps) => (
        <Box
            sx={(t) => ({
                border: `1px solid ${t.palette.divider}`,
                borderRadius: 1,
                overflow: "hidden"
            })}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "140px 1fr auto", sm: "180px 1fr auto" },
                    alignItems: "stretch",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={(t) => ({
                        bgcolor:
                            t.palette.mode === "dark"
                                ? t.palette.grey[800]
                                : t.palette.grey[100],
                        px: 1,
                        py: 0.75,
                        borderRight: `1px solid ${t.palette.divider}`,
                        display: "flex",
                        alignItems: "center",
                    })}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}
                    >
                        <Localized id={labelId}><span /></Localized>
                    </Typography>
                </Box>
                <Box
                    sx={(t) => ({
                        bgcolor: t.palette.background.paper,
                        px: 1,
                        py: 0.75,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                    })}
                >
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.5, wordBreak: "break-word" }}
                        title={typeof value === "string" ? value : undefined}
                    >
                        {valueNode ? (
                            <Box component="span">
                                {valueNode}
                            </Box>
                        ) : value ? (
                            value
                        ) : (
                            <Box component="span" sx={{ color: "text.disabled", fontWeight: 400 }}>
                                <Localized id="missing">Brak</Localized>
                            </Box>
                        )}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        px: 0.5,
                        "&:hover .editIcon, &:focus-within .editIcon": {
                            color: "info.main",
                        },
                    }}
                >
                    <EditIconButton
                        tooltip={<Localized id='edit'>_edit</Localized>}
                        onClick={() => openEdit(field, value)}
                    />
                </Box>
            </Box>
        </Box>
    );


    return (
        <>
            <TableContainer component={Paper}>
                <Stack spacing={1} sx={{ mb: 1 }}>
                    <FieldRow field="no" labelId="process-no" value={process.no} />
                    <FieldRow field="name" labelId="process-name" value={process.name} />
                    {"scenario" in process && (
                        <FieldRow
                            field="scenario"
                            labelId="process-scenario"
                            value={process.scenario}
                            valueNode={<ValidationScenarioLabel value={process.scenario as any} />}
                        />
                    )}
                    <FieldRow field="description" labelId="process-description" value={process.description} />

                </Stack>

                <Divider sx={{ my: 1.5 }} />
                <Box
                    sx={(t) => ({
                        p: 1.25,
                        borderRadius: 1.5,
                        border: `1px solid ${t.palette.divider}`,
                    })}
                >
                    {/* Nagłówek sekcji */}
                    <Box
                        sx={(t) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                            pb: 0.5,
                            bgcolor: alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.05 : 0.04),

                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                        })}
                    >
                        <PendingActionsRounded sx={{ fontSize: 18, color: "text.secondary" }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            <Localized id="process-management-panel">
                                <span>_Zarządzanie procesem</span>
                            </Localized>
                        </Typography>
                    </Box>

                    {/* Przyciski akcji */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        alignItems="center"
                    >
                        <Tooltip title={<Localized id="project-process-start">Rozpocznij / Wznów</Localized>}>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<PlayArrowRounded />}
                                    disabled={!canStart}
                                    onClick={() => openActionDialogFor("startProcess")}
                                >
                                    <Localized id="project-process-start">Rozpocznij</Localized>
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title={<Localized id="project-process-pause">Wstrzymaj</Localized>}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<PauseRounded />}
                                    disabled={!canPause}
                                    onClick={() => openActionDialogFor("pauseProcess")}
                                >
                                    <Localized id="project-process-pause">Wstrzymaj</Localized>
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={<Localized id="project-process-cancel">Anuluj</Localized>}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<CancelRounded />}
                                    disabled={!canPause}
                                    onClick={() => openActionDialogFor("cancelProcess")}
                                >
                                    <Localized id="project-process-cancel">Anuluj</Localized>
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={<Localized id="project-process-finish">Zakończ</Localized>}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<CheckCircleRounded />}
                                    disabled={!canFinish}
                                    onClick={() => openActionDialogFor("finishProcess")}
                                >
                                    <Localized id="project-process-finish">Zakończ</Localized>
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteOutlineRounded />}
                                    disabled={!canDelete}
                                    onClick={() => setOpenDelete(true)}
                                >
                                    <Localized id="delete">Usuń</Localized>
                                </Button>
                            </span>
                        </Tooltip>

                    </Stack>
                </Box>
            </TableContainer>

            {editField && (
                <EditProcessDescriptionField
                    processId={process.id}
                    field={editField}
                    value={editValue}
                    onSave={(updatedProjectHeader) => {
                        setEditField(null);
                        dispatch(showSuccess("saved"))
                        onSave(updatedProjectHeader as unknown as ProjectProcess, false);
                    }}
                    onCancel={closeEdit}
                />
            )}
            {openActionDialog && selectedAction && (
                <PerformActionOnProcessDialog
                    process={process}
                    actionType={selectedAction}
                    onCancel={closeActionDialog}
                    onSuccess={(updatedProjectHeader) => {
                        setOpenActionDialog(false);
                        onSave(updatedProjectHeader as unknown as ProjectProcess, true);
                    }} />
            )}
            {openDelete && (
                <DeleteValidationProcess
                    processId={process.id}
                    onSuccessfullDeleted={handleProcessDeleted}
                    onCancel={() => setOpenDelete(false)}
                />
            )}

        </>
    );
};
